import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    NJButton,
    NJIcon,
    NJIconButton,
    NJMenu,
    NJSegmentedControl,
    NJSegmentedControlButton,
    NJSpinner,
    NJText,
    NJToastContainer,
    NJTooltip,
} from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { NETWORK_PAGES, NetworkShell, Size2MixAppShell, Size2MixNetworkIcon, Size2MixSecondaryNav, Size2MixSecondaryNavTitle, Size2MixAccordion, Size2MixInfoGrid, Size2MixSection, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import { KIND_LABEL, OWNER_ID, ROLE_LABEL, USERS } from './data';
import { useSharingActions, useSharingState, type SharingActions } from './hooks';
import { accessEntries, computePermissions, findPrincipal, isViewerId, VIEWER_IDS, VIEWERS } from './permissions';
import { ContractField, INITIAL_INFO, useInfoRows, type NetworkInfo } from './info';
import { NoAccess } from './no-access';
import { NotificationCenter } from './notifications';
import { AvatarStack, PrincipalAvatar, run, successToast } from './parts';
import { AccessPopover, ShareModal, type SurfaceStep } from './surfaces';
import type { SharingPermissions, ViewerId } from './types';
import './sharing.css';

export const meta = {
    title: 'Partage du réseau',
    description: 'Partager un réseau depuis Général comme dans Figma : pile d’avatars dans la barre d’actions, aperçu « Qui a accès », fenêtre de partage, organigramme (équipe, filière, territoire, GBU) et équipes AO transversales. ?as=owner|write|read.',
    tags: ['network', 'detail', 'poc'],
    author: 'dv5886',
    status: 'draft' as const,
    category: 'general',
    createdAt: '2026-10-21',
};

type Surface = 'none' | 'popover' | 'modal';
type PageId = (typeof NETWORK_PAGES)[number]['id'];
const FLUID_TYPE = 'hot_water' as const;

function isPageId(value: string | null): value is PageId {
    return NETWORK_PAGES.some((p) => p.id === value);
}

/** Simulates opening a direct link to any page of the network (only meaningful without access). */
function LinkPicker({ page, onPage }: { page: PageId; onPage: (id: PageId) => void }) {
    const label = NETWORK_PAGES.find((p) => p.id === page)?.label;
    return (
        <div className="ns-review__link">
            <NJText scale="xs" variant="secondary">Lien ouvert :</NJText>
            <NJMenu.Root>
                <NJMenu.Anchor>
                    <button type="button" className="ns-select ns-select--outlined" aria-label={`Lien ouvert : ${label}`}>
                        <NJText scale="sm">{label}</NJText>
                        <NJIcon name="expand_more" size="sm" />
                    </button>
                </NJMenu.Anchor>
                <NJMenu.Dropdown>
                    <NJMenu.Group>
                        {NETWORK_PAGES.map((p) => (
                            <NJMenu.Item key={p.id} label={p.label} selected={p.id === page} leading={<NJIcon name={p.icon} size="sm" />} onClick={() => onPage(p.id)} />
                        ))}
                    </NJMenu.Group>
                </NJMenu.Dropdown>
            </NJMenu.Root>
        </div>
    );
}

/** Prototype-only panel (not part of Nemo): persona and starting data. */
function PrototypeControls({ viewerId, perms, onViewer, actions, page, onPage }: { viewerId: ViewerId; perms?: SharingPermissions; onViewer: (id: ViewerId) => void; actions: SharingActions; page: PageId; onPage: (id: PageId) => void }) {
    const [open, setOpen] = useState(true);
    const viewer = VIEWERS[viewerId];
    const rights = perms?.role ? `${ROLE_LABEL[perms.role]}${perms.via.length ? ` via ${perms.via.join(', ')}` : ''}` : 'Aucun accès';
    return (
        <aside className="ns-review" aria-label="Contrôles du prototype">
            <div className="ns-review__head">
                <NJIcon name="science" size="sm" />
                <NJText scale="sm" className="ns-strong ns-grow">Prototype — {open ? 'Voir en tant que' : viewer.label}</NJText>
                <NJIconButton icon={open ? 'expand_more' : 'expand_less'} label={open ? 'Réduire les contrôles du prototype' : 'Ouvrir les contrôles du prototype'} size="sm" onClick={() => setOpen((o) => !o)} />
            </div>
            {open && (
                <div className="ns-review__body">
                    <NJSegmentedControl key={viewerId} label="Voir en tant que" size="sm" value={viewerId} onChange={(v) => isViewerId(v) && onViewer(v)}>
                        {VIEWER_IDS.map((id) => <NJSegmentedControlButton key={id} value={id} label={VIEWERS[id].label} />)}
                    </NJSegmentedControl>
                    <NJText scale="xs" variant="secondary">{viewer.description} Droit calculé : <strong>{rights}</strong>.</NJText>
                    {perms && !perms.canRead && <LinkPicker page={page} onPage={onPage} />}
                    <div className="ns-review__actions">
                        <NJButton label="Repartir d’un réseau privé" variant="secondary" emphasis="minimal" scale="sm" icon="lock" onClick={() => run(() => actions.reset.mutateAsync('private'), 'Réseau remis en privé')} />
                        <NJButton label="Réseau déjà partagé" variant="secondary" emphasis="minimal" scale="sm" icon="group" onClick={() => run(() => actions.reset.mutateAsync('shared'), 'Réseau partagé à l’équipe AO et à la filière')} />
                    </div>
                </div>
            )}
        </aside>
    );
}

export default function NetworkSharing() {
    const nav = useSize2MixRefNavigation();
    const [params, setParams] = useSearchParams();
    const asParam = params.get('as');
    const viewerId: ViewerId = isViewerId(asParam) ? asParam : 'owner';
    const viewer = VIEWERS[viewerId];
    const pageParam = params.get('page');
    const page: PageId = isPageId(pageParam) ? pageParam : 'general';
    const setPage = (id: PageId) => {
        const next = new URLSearchParams(params);
        next.set('page', id);
        setParams(next, { replace: true });
    };
    const { data: state, isLoading } = useSharingState();
    const actions = useSharingActions(viewerId);
    const [surface, setSurface] = useState<Surface>('none');
    const shellRef = useRef<HTMLDivElement>(null);
    const [info, setInfo] = useState<NetworkInfo>(INITIAL_INFO);
    const [contract, setContract] = useState('Les Tilleuls');
    const [isReference, setIsReference] = useState(false);
    const NETWORK = { name: info.name, fluidType: FLUID_TYPE };
    const [step, setStep] = useState<SurfaceStep>('sharing');

    const perms = state ? computePermissions(state, viewer) : undefined;
    // Owner and « Peut modifier » edit the information; only the owner manages access.
    const canEdit = perms?.role === 'owner' || perms?.role === 'write';
    const infoRows = useInfoRows(info, (patch) => setInfo((i) => ({ ...i, ...patch })), canEdit);
    const referenceTooltip = !contract
        ? 'La promotion en référence nécessite un contrat associé'
        : isReference ? 'Ce réseau est la référence de son contrat'
        : !canEdit ? 'Vous n’avez pas les droits pour promouvoir ce réseau' : 'Promouvoir en réseau de référence';
    const entries = state ? accessEntries(state) : [];
    const shared = entries.length > 1;
    const owner = state ? findPrincipal(state, state.ownerId) : undefined;

    const openSurface = (next: Surface) => {
        setStep('sharing');
        setSurface(next);
    };
    const close = () => openSurface('none');
    const setViewer = (id: ViewerId) => {
        const next = new URLSearchParams(params);
        next.set('as', id);
        setParams(next, { replace: true });
        close();
    };

    let status = 'Privé · visible par vous uniquement';
    if (perms?.role === 'owner' && shared) status = `Partagé · ${entries.length - 1} accès en plus du vôtre`;
    if (perms?.role === 'write' || perms?.role === 'read') status = `${ROLE_LABEL[perms.role]} · via ${perms.via.join(', ')}`;

    const surfaceProps = state && perms ? { state, entries, perms, viewerUserId: viewer.userId, actions, step, setStep, onClose: close } : undefined;

    let content;
    if (isLoading || !state || !perms || !surfaceProps) {
        content = <div className="ns-center"><NJSpinner scale="md" isLoading /></div>;
    } else if (!perms.canRead) {
        content = null;
    } else {
        const toolbar = (
            <div className="ns-toolbar">
                <div className="ns-toolbar__left">
                    <NJIcon name={shared ? 'group' : 'lock'} size="sm" />
                    <NJText scale="sm" variant="secondary">{status}</NJText>
                </div>
                <div className="ns-toolbar__right">
                    <div className="ns-anchor">
                        <AvatarStack entries={entries} onClick={() => openSurface(surface === 'popover' ? 'none' : 'popover')} active={surface === 'popover'} />
                        {perms.canManage && state.requests.length > 0 && (
                            <NJButton label={state.requests.length > 1 ? `${state.requests.length} demandes d’accès` : '1 demande d’accès'} variant="secondary" emphasis="subtle" icon="person_add" scale="md" onClick={() => openSurface('modal')} />
                        )}
                        {perms.canManage && <NJButton label="Partager" icon={shared ? 'group' : 'lock'} scale="md" onClick={() => openSurface('modal')} />}
                        {surface === 'popover' && <AccessPopover {...surfaceProps} onManage={() => openSurface('modal')} />}
                    </div>
                    <span className="ns-divider" />
                    <NJTooltip text="Ancienne page réseau"><NJIconButton icon="view_timeline" label="Ancienne page réseau" variant="tertiary" scale="lg" /></NJTooltip>
                    <NJTooltip text="Ajouter aux favoris"><NJIconButton icon="favorite_border" label="Ajouter aux favoris" variant="tertiary" scale="lg" /></NJTooltip>
                    {perms.canRead && (
                        <NJTooltip text={referenceTooltip}>
                            <span>
                                <NJIconButton
                                    icon="emoji_events"
                                    label={referenceTooltip}
                                    variant="tertiary"
                                    scale="lg"
                                    isDisabled={!canEdit || !contract || isReference}
                                    onClick={() => { setIsReference(true); successToast('Réseau promu en référence du contrat'); }}
                                />
                            </span>
                        </NJTooltip>
                    )}
                    <NJTooltip text="Dupliquer"><NJIconButton icon="add_to_photos" label="Dupliquer" variant="tertiary" scale="lg" /></NJTooltip>
                    {perms.canManage && <NJTooltip text="Supprimer"><NJIconButton icon="delete_outline" label="Supprimer" variant="tertiary" scale="lg" /></NJTooltip>}
                </div>
            </div>
        );
        content = (
            <>
                {toolbar}
                <div className="ns-body">
                    <Size2MixAccordion>
                        <NJText scale="sm" variant="secondary">Réseau créé par {USERS.find((u) => u.id === OWNER_ID)?.name} le 12/09/2026, dernière modification le 20/10/2026{owner && owner.id !== OWNER_ID ? ` · Propriétaire : ${owner.name}` : ''}</NJText>
                        <Size2MixSection title="Informations du réseau" icon="info">
                            <Size2MixInfoGrid rows={[
                                ...infoRows,
                                { label: 'Contrat', value: <ContractField canEdit={canEdit && !isReference} contract={contract} setContract={setContract} /> },
                                {
                                    label: 'Accès',
                                    value: (
                                        <span className="ns-inline-access">
                                            {entries.map(({ principal }) => (
                                                <NJTooltip key={principal.id} text={principal.kind === 'user' ? principal.name : `${principal.name} · ${KIND_LABEL[principal.kind]}`}>
                                                    <span className="ns-avatar-hit" tabIndex={0} aria-label={principal.name}>
                                                        <PrincipalAvatar principal={principal} scale="sm" />
                                                    </span>
                                                </NJTooltip>
                                            ))}
                                        </span>
                                    ),
                                },
                            ]} />
                        </Size2MixSection>
                    </Size2MixAccordion>
                </div>
                {surface === 'modal' && perms.canManage && <ShareModal networkName={info.name} {...surfaceProps} />}
            </>
        );
    }

    const notifications = state && perms ? <NotificationCenter networkName={info.name} key={viewerId} rootRef={shellRef} state={state} isOwner={perms.canManage} onManage={() => openSurface('modal')} /> : null;
    const controls = <PrototypeControls viewerId={viewerId} perms={perms} onViewer={setViewer} actions={actions} page={page} onPage={setPage} />;

    if (state && perms && !perms.canRead) {
        // Direct link without access: the network menu stays visible with the opened page selected, other pages disabled.
        const items = NETWORK_PAGES.map((p) => ({ ...p, isDisabled: p.id !== page }));
        return (
            <PrototypeStudioLayout title={meta.title}>
                <NJToastContainer />
                {controls}
                {notifications}
                <div ref={shellRef} className="ns-contents">
                <Size2MixAppShell
                    activePrimary="contracts"
                    contentPadding={0}
                    onPrimaryNavigate={nav.onPrimaryNavigate}
                    secondaryNav={<Size2MixSecondaryNav header={<Size2MixSecondaryNavTitle icon={<Size2MixNetworkIcon fluidType={NETWORK.fluidType} />} title={NETWORK.name} />} items={items} activeId={page} />}
                >
                    <NoAccess state={state} viewerUserId={viewer.userId} ownerName={owner?.name ?? 'la propriétaire'} pageLabel={NETWORK_PAGES.find((p) => p.id === page)?.label ?? 'Général'} actions={actions} />
                </Size2MixAppShell>
                </div>
            </PrototypeStudioLayout>
        );
    }

    return (
        <PrototypeStudioLayout title={meta.title}>
            <NJToastContainer />
            {controls}
            {notifications}
            <div ref={shellRef} className="ns-contents">
            <NetworkShell network={NETWORK} activePage="general" onNavigate={nav.onNetworkNavigate} onPrimaryNavigate={nav.onPrimaryNavigate}>
                <div className="ns-stage">
                    <div className="ns-stage__main">{content}</div>
                </div>
            </NetworkShell>
            </div>
        </PrototypeStudioLayout>
    );
}
